/* eslint-disable react-native/no-inline-styles */
/* eslint-disable curly */
import React, { useEffect, useRef, useState } from 'react';
import { ScrollView, StyleSheet, View } from 'react-native';
import { SafeAreaView } from 'react-native-safe-area-context';
import { Button, Text, TextInput } from 'react-native-paper';
import { theme } from '../theme/theme';
import { useDispatch, useSelector } from 'react-redux';
import { doPost } from '../services/http';
import { checkMobile, errorHandler, forgotPassword, getCompanyInfo, getProfile } from '../services/Utils';
import { hideLoader, setOTPInfo, showLoader, showToast } from '../store/info.slice';
import * as storage from '../services/storage';
import RecaptchaV2 from '../components/RecaptchaV2';
import RecaptchaNotice from '../components/RecaptchaNotice';
import { getRecaptchaChallenge, getRecaptchaHeaders } from '../services/Recaptcha';

const InputRoundness = 6;
const RESEND_TIME = 10;
const OTP_LENGTH = 6;

function OTP({ navigation }) {
    const dispatch = useDispatch();
    const info = useSelector((state) => state.info?.info);
    const settings = info?.settings;
    const disable_mobile = settings?.disable_mobile;
    const _otpInfo = useSelector((state) => state.info?.otpInfo);
    const [otpInfo, setOtpInfo] = useState(_otpInfo);
    const [otp, setOtp] = useState(Array(OTP_LENGTH).fill(''));
    const [sendCount, setResendCount] = useState(1);
    const [resendTimer, setResendTimer] = useState(RESEND_TIME);
    const [isResendDisabled, setIsResendDisabled] = useState(true);
    const [isSubmitDisabled, setIsSubmitDisabled] = useState(true);
    const [captchaChallenge, setCaptchaChallenge] = useState(null);
    const [captchaV2Token, setCaptchaV2Token] = useState('');
    const inputsRef = useRef([]);

    useEffect(() => {
        // Start countdown timer for resend
        if (resendTimer > 0) {
            const interval = setInterval(() => {
                setResendTimer((prev) => prev - 1);
            }, 1000);
            return () => clearInterval(interval);
        } else {
            setIsResendDisabled(false);
        }
    }, [resendTimer]);

    useEffect(() => {
        // Enable submit button only when all OTP fields are filled
        setIsSubmitDisabled(otp.some((digit) => digit === ''));
    }, [otp]);

    const handleChange = (text, index) => {
        if (text.length > 1) return; // Prevent multiple characters
        const newOtp = [...otp];
        newOtp[index] = text;
        setOtp(newOtp);

        // Move to next input if filled
        if (text && index < OTP_LENGTH - 1) {
            inputsRef.current[index + 1]?.focus();
        }

        // Submit OTP if all boxes are filled
        if (newOtp.every((digit) => digit !== '')) {
            // onSubmit(newOtp.join(""));
        }
    };

    const handleKeyPress = (e, index) => {
        if (e.nativeEvent.key === 'Backspace' && !otp[index] && index > 0) {
            inputsRef.current[index - 1]?.focus();
        }
    };

    const handleResend = () => {
        if (captchaChallenge && !captchaV2Token) { return; }
        if (sendCount < 3) {
            setOtp(Array(OTP_LENGTH).fill(''));
            resendHandler();
        } else {
            dispatch(showToast('Maximum resend limit reached'));
        }
    };

    const resendHandler = async () => {
        let params = {};
        if (!disable_mobile) {
            params.mobile = otpInfo.mobile;
        } else {
            params.email = otpInfo.email;
        }
        if (otpInfo?.action === 'register') {
            checkMobile({ ...params, action: 'register' }).then(response => {
                setResendCount((count) => count + 1);
                setResendTimer(RESEND_TIME);
                setIsResendDisabled(true);
                setOtpInfo({ ...otpInfo, token: response.token });
                dispatch(showToast(response?.message));
            }).catch(error => {
                errorHandler(error);
            });
        } else if (otpInfo?.action === 'forgot-password') {
            dispatch(showLoader());
            const headers = await getRecaptchaHeaders(info?.recaptcha, 'cash_counter_forgot_password', captchaV2Token);
            forgotPassword(params, { headers }).then(response => {
                dispatch(hideLoader());
                setResendCount((count) => count + 1);
                setResendTimer(RESEND_TIME);
                setIsResendDisabled(true);
                setCaptchaChallenge(null);
                setCaptchaV2Token('');
                setOtpInfo({ ...otpInfo, token: response.token });
                dispatch(showToast(response?.message));
            }).catch(error => {
                dispatch(hideLoader());
                const challenge = getRecaptchaChallenge(error, info?.recaptcha);
                if (challenge) {
                    setCaptchaChallenge(challenge);
                    setCaptchaV2Token('');
                    setIsResendDisabled(false);
                    return;
                }
                errorHandler(error);
            });
        }
    };

    const onSubmitHandler = () => {
        if (otpInfo?.action === 'register') {
            register();
        } else if (otpInfo?.action === 'forgot-password') {
            verifyOTP();
        }
    };

    const register = async () => {
        try {
            dispatch(showLoader());
            const response = await doPost('register', { ...otpInfo, otp: otp.join('') });
            storage.storeData('accessToken', response.data.accessToken);
            await getCompanyInfo();
            const customer = await getProfile();
            dispatch(hideLoader());
            if (!customer.cafe_id) {
                navigation.navigate('ChangeLocation');
            } else {
                navigation.navigate('Home');
            }
        } catch (error) {
            console.error(error);
            errorHandler(error);
        }
    };

    const verifyOTP = () => {
        dispatch(showLoader());
        doPost('/otp/verify', { ...otpInfo, otp: otp.join('') }).then(response => {
            dispatch(hideLoader());
            dispatch(setOTPInfo({ ...otpInfo, otp: otp.join('') }));
            navigation.push('ResetPassword');
        }).catch(error => {
            console.error(error);
            errorHandler(error);
        });
    };

    const getOTPText = () => {
        if (otpInfo?.action === 'register') {
            if (otpInfo?.primary_identifier === 'email' && !!otpInfo?.email) {
                return 'Email address';
            } else {
                return 'Mobile number';
            }
        } else if (otpInfo?.action === 'forgot-password') {
            if (otpInfo?.primary_identifier === 'email' && !!otpInfo?.mobile) {
                return 'Email address';
            } else {
                return 'Mobile number';
            }
        } else {
            return 'Email/Mobile number';
        }
    };

    return (
        <SafeAreaView edges={['bottom']} style={[styles.container, { backgroundColor: theme.colors.primary }]}>
            {/* <IconButton icon="arrow-left" size={24} iconColor="#ffffff" onPress={() => navigation.goBack()} /> */}
            <View style={{ padding: 8, flexDirection: 'row', alignItems: 'center' }}>
                {/* <IconButton icon="arrow-left" size={24} iconColor="#ffffff" onPress={() => navigation.goBack()} /> */}
                <View style={{ marginLeft: 16, flex: 1 }}>
                    <Text variant="titleLargeBold" style={{ color: '#ffffff' }}>OTP Verification</Text>
                    <Text variant="textLight" style={{ color: '#ffffff' }}>Enter the {OTP_LENGTH}-digit OTP sent to your {getOTPText()}</Text>
                </View>
            </View>
            <View style={styles.mainContainer}>
                <ScrollView contentContainerStyle={styles.KeyboardAwareScrollView}>
                    <View style={{ marginBottom: 8 }}>
                        <Text variant="titleMedium" style={{ textAlign: 'center' }}>Enter OTP</Text>
                        <Text variant="textLight" style={{ textAlign: 'center' }}>Enter the {OTP_LENGTH}-digit OTP sent to your {getOTPText()}</Text>
                    </View>
                    <View style={styles.otpContainer}>
                        {otp.map((digit, index) => {
                            return (
                                <TextInput
                                    key={index}
                                    ref={(el) => (inputsRef.current[index] = el)}
                                    style={styles.input}
                                    keyboardType="tel"
                                    inputMode="tel"
                                    mode="outlined"
                                    maxLength={1}
                                    value={digit}
                                    onChangeText={(text) => handleChange(text, index)}
                                    onKeyPress={(e) => handleKeyPress(e, index)}
                                    autoFocus={index === 0}
                                    theme={{ roundness: InputRoundness }}
                                    contentStyle={styles.contentStyle}
                                />);
                        }
                        )}
                    </View>
                    <View style={styles.inputContainer}>
                        <Button theme={{ roundness: 36 }} onPress={onSubmitHandler}
                            disabled={isSubmitDisabled} mode="contained" labelStyle={styles.labelStyle}>Verify</Button>
                    </View>
                    <View style={styles.resendContainer}>
                        <Text style={styles.timerText}>
                            {isResendDisabled ? `Resend OTP in ${resendTimer}s` : ''}
                        </Text>
                        <Button
                            mode="text"
                            onPress={handleResend}
                            disabled={isResendDisabled}
                        >
                            Resend OTP
                        </Button>
                    </View>
                    {captchaChallenge?.siteKey ? (
                        <RecaptchaV2
                            key={`${captchaChallenge.code}-${captchaChallenge.siteKey}-${captchaChallenge.attempt}`}
                            siteKey={captchaChallenge.siteKey}
                            onToken={setCaptchaV2Token}
                        />
                    ) : null}
                    <RecaptchaNotice visible={info?.recaptcha?.enabled} />
                </ScrollView>
            </View>
        </SafeAreaView>
    );
}

const styles = StyleSheet.create({
    container: {
        flex: 1,
        // backgroundColor: theme.colors.primary,
    },
    mainContainer: {
        borderRadius: 36,
        backgroundColor: '#ffffff',
        padding: 16,
        margin: 8,
    },
    KeyboardAwareScrollView: {
        flexGrow: 1,
        borderRadius: 36,
        backgroundColor: '#ffffff',
        padding: 8,
    },
    otpContainer: {
        flexDirection: 'row',
        justifyContent: 'center',
        marginBottom: 16,
        gap: 10,
    },
    input: {
        backgroundColor: '#f6f8fa',
        marginVertical: 8,
        width: 42,
        height: 42,
        fontSize: 14,
        textAlign: 'center',
    },
    contentStyle: {
        marginLeft: -12,
        textAlign: 'center',
    },
    labelStyle: {
        fontSize: 16,
        letterSpacing: 2,
        paddingVertical: 4,
    },
    inputContainer: {
        marginVertical: 8,
    },
    resendContainer: { alignItems: 'center', marginTop: 10 },
    timerText: { fontSize: 14, color: 'gray' },
});

export default OTP;
